import { type ReactNode } from 'react';
import classnames from 'classnames';

import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';

import styles from './TradeBenefitsFaq.scss';
import dibsCss from 'dibs-css';

export type FaqItem = {
    id: string;
    question: ReactNode;
    answer: ReactNode;
};

const Paragraph = ({ children }: { children: ReactNode }): ReactNode => (
    <p className={dibsCss.m0}>{children}</p>
);

const BulletList = ({ children }: { children: ReactNode }): ReactNode => (
    <ul className={classnames(styles.bulletList, dibsCss.plLarge)}>{children}</ul>
);

const richTextValues = {
    b: (content: ReactNode) => <b>{content}</b>,
    contactLink: (content: ReactNode) => (
        <Link href="/contact" target="_blank">
            {content}
        </Link>
    ),
    returnPolicyLink: (content: ReactNode) => (
        <Link href="/info/trade/returns/" target="_blank">
            {content}
        </Link>
    ),
    moneyBackLink: (content: ReactNode) => (
        <Link href="/info/trade/promise/" target="_blank">
            {content}
        </Link>
    ),
};

export const getFaqs = (): FaqItem[] => [
    {
        id: 'how-it-works',
        question: (
            <FormattedMessage
                id="abt.TradeBenefitsFaq.howItWorks.question"
                defaultMessage="How does the Trade 1st Program work?"
            />
        ),
        answer: (
            <>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.howItWorks.intro"
                        defaultMessage="As a member of the Trade 1st Program, you’ll enjoy the benefits dedicated to helping you and your team fulfill your vision and create something beautiful. You’ll have access to an unrivaled supply of furniture, lighting, décor and art from vetted sellers around the world at exclusive net prices, service tailored to your needs, comprehensive purchase protection that offers peace mind and publicity opportunities with 1stDibs customers."
                    />
                </Paragraph>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.howItWorks.accumulate"
                        defaultMessage="Each time you shop 1stDibs, you’ll accumulate points that help you reach a higher membership status and access richer benefits."
                    />
                </Paragraph>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.howItWorks.howWorks"
                        defaultMessage="Here’s how it works:"
                    />
                </Paragraph>
                <BulletList>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.howItWorks.bulletPoints"
                            defaultMessage="Earn {pointPerSpend} point for every {amount} spent"
                            values={{
                                pointPerSpend: <FormattedNumber value={1} />,
                                amount: (
                                    <FormattedNumber
                                        value={1}
                                        style="currency"
                                        currency="USD"
                                        maximumFractionDigits={0}
                                    />
                                ),
                            }}
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.howItWorks.bulletSilver"
                            defaultMessage="Earn {points} points to achieve Silver status"
                            values={{ points: <FormattedNumber value={25000} /> }}
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.howItWorks.bulletGold"
                            defaultMessage="Earn {points} points to achieve Gold status"
                            values={{ points: <FormattedNumber value={100000} /> }}
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.howItWorks.bulletPlatinum"
                            defaultMessage="Earn {points} points to achieve Platinum status"
                            values={{ points: <FormattedNumber value={250000} /> }}
                        />
                    </li>
                </BulletList>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.howItWorks.closing"
                        defaultMessage="You will only earn points for all confirmed purchases; shipping and taxes are not included, and points will be deducted if a purchase is canceled or returned. Purchases made by all enrolled colleagues of your firm will count toward your firm’s collective rewards."
                    />
                </Paragraph>
            </>
        ),
    },
    {
        id: 'status-duration',
        question: (
            <FormattedMessage
                id="abt.TradeBenefitsFaq.statusDuration.question"
                defaultMessage="How long does my status last?"
            />
        ),
        answer: (
            <Paragraph>
                <FormattedMessage
                    id="abt.TradeBenefitsFaq.statusDuration.answer"
                    defaultMessage="Your Silver, Gold or Platinum status will last at least one calendar year. Status is based on points earned in the previous calendar year and the current year. So, if you earned Silver status in 2023, it will carry over in 2024. If you then achieve Gold in 2024, you will be Gold for the rest of 2024 and 2025."
                />
            </Paragraph>
        ),
    },
    {
        id: 'points-expiration',
        question: (
            <FormattedMessage
                id="abt.TradeBenefitsFaq.pointsExpiration.question"
                defaultMessage="Do points expire?"
            />
        ),
        answer: (
            <Paragraph>
                <FormattedMessage
                    id="abt.TradeBenefitsFaq.pointsExpiration.answer"
                    defaultMessage="On January 1, points towards your firm status reset to zero. Any status earned in the previous year will carry over to the next year. Remaining cash back points towards your next cash reward under {threshold} will also expire on January 1."
                    values={{ threshold: <FormattedNumber value={5000} /> }}
                />
            </Paragraph>
        ),
    },
    {
        id: 'contact-help',
        question: (
            <FormattedMessage
                id="abt.TradeBenefitsFaq.contactHelp.question"
                defaultMessage="How do I connect with a person to get help with a purchase or answer other questions I have?"
            />
        ),
        answer: (
            <>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.contactHelp.intro"
                        defaultMessage="You can count on the support of our in-house experts who understand the complexities of trade projects, and are equipped to help you at every step — from navigating the supply chain to finding the right piece to managing its safe delivery. Plus, you can unlock access to a Dedicated Account Representative when you achieve Silver status."
                    />
                </Paragraph>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.contactHelp.link"
                        defaultMessage="<contactLink>Click here</contactLink> to contact us for any of your questions or purchase needs."
                        values={{ contactLink: richTextValues.contactLink }}
                    />
                </Paragraph>
            </>
        ),
    },
    {
        id: 'return-policy',
        question: (
            <FormattedMessage
                id="abt.TradeBenefitsFaq.returnPolicy.question"
                defaultMessage="What is the Return Policy for Trade Members?"
            />
        ),
        answer: (
            <>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.returnPolicy.intro"
                        defaultMessage="Trade 1st members enjoy enhanced benefits that allow them to shop with confidence, including a 14-Day Return Policy."
                    />
                </Paragraph>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.returnPolicy.details"
                        defaultMessage="As part of this policy, you have a minimum of 14 days to send back qualifying items — even if the seller doesn’t accept returns. Additionally, we’ll cover the restocking fees when you send back a qualifying item within the Trade Return Policy window."
                    />
                </Paragraph>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.returnPolicy.link"
                        defaultMessage="<returnPolicyLink>Click here</returnPolicyLink> for details."
                        values={{ returnPolicyLink: richTextValues.returnPolicyLink }}
                    />
                </Paragraph>
            </>
        ),
    },
    {
        id: 'damaged-item',
        question: (
            <FormattedMessage
                id="abt.TradeBenefitsFaq.damagedItem.question"
                defaultMessage="What happens if an item I purchased arrives damaged or not as described?"
            />
        ),
        answer: (
            <>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.damagedItem.intro"
                        defaultMessage="In the rare event your order is not as described, is damaged in transit or fails to arrive, you may be eligible for a full refund if you contact us within 120 days*, depending on your Trade 1st status:"
                    />
                </Paragraph>
                <BulletList>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.damagedItem.bulletBronze"
                            defaultMessage="Bronze: 30 days"
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.damagedItem.bulletSilver"
                            defaultMessage="Silver: 60 days"
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.damagedItem.bulletGold"
                            defaultMessage="Gold: 90 days"
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.damagedItem.bulletPlatinum"
                            defaultMessage="Platinum: 120 days"
                        />
                    </li>
                </BulletList>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.damagedItem.link"
                        defaultMessage="<moneyBackLink>Click here</moneyBackLink> to learn more."
                        values={{ moneyBackLink: richTextValues.moneyBackLink }}
                    />
                </Paragraph>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.damagedItem.effective"
                        defaultMessage="*Effective January 7, 2025"
                    />
                </Paragraph>
            </>
        ),
    },
    {
        id: 'marketing-opportunities',
        question: (
            <FormattedMessage
                id="abt.TradeBenefitsFaq.marketingOpportunities.question"
                defaultMessage="What marketing opportunities are available to my firm through 1stDibs?"
            />
        ),
        answer: (
            <>
                <Paragraph>
                    <FormattedMessage
                        id="abt.TradeBenefitsFaq.marketingOpportunities.intro"
                        defaultMessage="Publicity on 1stDibs may include the following placements:"
                    />
                </Paragraph>
                <BulletList>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.marketingOpportunities.bulletPortfolio"
                            defaultMessage="<b>Designer Portfolio:</b> Put your firm’s portfolio in front of high-net-worth customers and potential new clients"
                            values={{ b: richTextValues.b }}
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.marketingOpportunities.bulletSocial"
                            defaultMessage="<b>Social Media:</b> Have your work featured on the 1stDibs Instagram, Facebook, Twitter or Pinterest account"
                            values={{ b: richTextValues.b }}
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.marketingOpportunities.bulletEmail"
                            defaultMessage="<b>Email:</b> Highlight one of your most recent projects in a 1stDibs email, which connects you with potential new clients and increases your brand exposure"
                            values={{ b: richTextValues.b }}
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.marketingOpportunities.bulletSite"
                            defaultMessage="<b>Site:</b> Feature your work across 1stDibs.com"
                            values={{ b: richTextValues.b }}
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.marketingOpportunities.bulletEditorial"
                            defaultMessage="<b>Editorial Coverage:</b> Our editorial features boost your visibility, highlighting your design work and showcasing your firm’s portfolio to an elevated customer base and potential new clients. This is available for Gold+ firms."
                            values={{ b: richTextValues.b }}
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="abt.TradeBenefitsFaq.marketingOpportunities.bullet1stDibs50"
                            defaultMessage="<b>1stDibs 50:</b> Qualify for inclusion in our annual list of the 50 most inspirational designers when you hit Platinum."
                            values={{ b: richTextValues.b }}
                        />
                    </li>
                </BulletList>
            </>
        ),
    },
];
